import { useState } from 'react';

import { downloadExport, type ExportResource } from '../api/exports';
import { exportParams } from '../api/exports-model';
import { useAuth } from '../auth/AuthContext';
import { hasAtLeast } from '../auth/roles';
import { Button } from './ui';

/**
 * «Экспорт XLSX» — the one button every list page of T.E1 owns.
 *
 * GATED MANAGER+ on purpose, exactly like the route: an export is a bulk PII
 * leave-the-system event, so a VIEWER gets no control to click, not a control
 * that answers 403. The api is still the authority — this gate only keeps the
 * UI honest.
 *
 * The failure surface is one line: the api's own message, next to the button,
 * announced politely. A refused export is a state the manager can act on (the
 * row cap says "narrow the filters"), so it is shown, not swallowed.
 */
export function ExportButton({
  resource,
  params,
  label = 'Экспорт XLSX',
  testId,
}: {
  resource: ExportResource;
  /** The current view's filters — pagination never travels (the api 400s it). */
  params: Record<string, string | number | boolean | undefined>;
  label?: string;
  testId: string;
}) {
  const { role } = useAuth();
  const [state, setState] = useState<'idle' | 'busy' | 'error'>('idle');
  const [message, setMessage] = useState<string | null>(null);

  if (!hasAtLeast(role, 'MANAGER')) return null;

  const run = async () => {
    setState('busy');
    setMessage(null);
    try {
      // `exportParams` states the file format and leaves the view filters verbatim —
      // the same query every page's list already sends, minus its pagination.
      await downloadExport(resource, exportParams(params));
      setState('idle');
    } catch (error) {
      setState('error');
      setMessage(error instanceof Error ? error.message : 'Не удалось выгрузить файл.');
    }
  };

  return (
    <span className="export-button" data-testid={`${testId}-wrap`}>
      <Button
        variant="secondary"
        size="sm"
        data-testid={testId}
        loading={state === 'busy'}
        onClick={() => void run()}
      >
        {label}
      </Button>
      {message !== null ? (
        <span className="export-button__error" role="alert" data-testid={`${testId}-error`}>
          {message}
        </span>
      ) : null}
    </span>
  );
}
